import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

// === API URL resolution ===
// In production (forkfinder.ca or any .emergent.host domain), ALWAYS use same-origin API calls.
// This prevents stale/cross-origin issues when REACT_APP_BACKEND_URL was baked-in at build time
// pointing to an old/preview URL.
const resolveApiBase = () => {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    // If we're on the public production domain or any *.emergent.host, use current origin
    if (host === "forkfinder.ca" || host === "www.forkfinder.ca" || host.endsWith(".emergent.host")) {
      return window.location.origin;
    }
  }
  return process.env.REACT_APP_BACKEND_URL;
};
const RESOLVED_BACKEND = resolveApiBase();
const API = `${RESOLVED_BACKEND}/api`;

// Axios global URL rewriter: any request to an outdated absolute URL gets rewritten
// to the current origin's /api path. This is a safety net for hard-coded URLs in older
// code paths.
axios.interceptors.request.use((config) => {
  if (typeof window === "undefined" || !config.url) return config;
  const currentHost = window.location.hostname;
  if (currentHost === "forkfinder.ca" || currentHost === "www.forkfinder.ca" || currentHost.endsWith(".emergent.host")) {
    try {
      const u = new URL(config.url, window.location.origin);
      // If the URL points to a different origin (preview, old deploys, etc.) but same path structure, rewrite
      if (u.origin !== window.location.origin && (u.pathname.startsWith("/api/") || u.hostname.endsWith(".emergent.host") || u.hostname.endsWith(".emergentagent.com"))) {
        config.url = `${window.location.origin}${u.pathname}${u.search}`;
      }
    } catch { /* not a parseable URL, skip */ }
  }
  return config;
});

const TOKEN_KEY = "forkfinder_admin_token";
const AuthContext = createContext();

// Set axios defaults for all requests
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
  });

  // Persist token to localStorage so it survives page navigation/refresh
  useEffect(() => {
    try {
      if (token) localStorage.setItem(TOKEN_KEY, token);
      else localStorage.removeItem(TOKEN_KEY);
    } catch { /* ignore */ }
  }, [token]);

  // Set up axios interceptor to attach Bearer token from latest state
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const stored = (() => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } })();
      const t = token || stored;
      if (t && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${t}`;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptor);
  }, [token]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      setUser(false);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    // Save token immediately to localStorage so the next request (auth/me, etc.) can use it
    try { localStorage.setItem(TOKEN_KEY, data.token); } catch { /* ignore */ }
    setUser(data);
    setToken(data.token);
    return data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch { /* ignore */ }
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    setUser(false);
    setToken(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, token }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
